<template>
  <div class="widget-iframe-container" :style="container_style">
    <div class="iframe-scaler" :style="wrapper_style">
      <div class="iframe-wrapper" :class="frame_class">
        <iframe
          v-if="widget_src"
          ref="widget-iframe"
          :key="iframe_key"
          :src="widget_src"
          :tabindex="disable_interactions ? '-1' : '0'"
          class="widget-iframe"
          frameborder="0"
          @load="onIframeLoad"
        />
        <div v-else class="iframe-placeholder">
          <v-icon size="48" color="grey lighten-1" aria-hidden="true">mdi-widgets</v-icon>
          <div class="text-caption text--secondary mt-2">
            {{ placeholder_text }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createParentMessageHandler, WIDGET_TO_PARENT } from '@/js/widget/widget-messaging.js';

/**
 * Reusable iframe wrapper for displaying a widget within a template card.
 * Handles scaling, responsive modes, messaging, and lifecycle.
 * Accepts configuration for live theme updates.
 *
 * @example
 * <widget-template-iframe
 *   :widget_config="widget_config"
 *   :send_settings="sendSettingsFunction"
 *   :send_page_change="sendPageChangeFunction"
 *   preview_mode="mobile"
 *   :scale="0.65"
 *   @widget-ready="onReady"
 *   @form-submitted="onSubmit"
 * />
 */
export default {
  name: 'WidgetTemplateIframe',

  props: {
    /**
     * Widget configuration object
     * Should contain everything needed to render the widget
     */
    widget_config: {
      type: Object,
      required: true,
    },

    /**
     * Function to build widget iframe src
     * Signature: (preview_mode, is_development) => string
     */
    build_src: {
      type: Function,
      required: true,
    },

    /**
     * Function to send settings to widget
     * Signature: (iframe_element, settings) => void
     */
    send_settings: {
      type: Function,
      required: true,
    },

    /**
     * Function to send page change to widget (optional)
     * Signature: (iframe_element, page) => void
     */
    send_page_change: {
      type: Function,
      default: null,
    },

    /**
     * Preview mode: 'tablet-landscape', 'tablet', 'mobile'
     */
    preview_mode: {
      type: String,
      default: 'tablet-landscape',
      validator: value => ['tablet-landscape', 'tablet', 'mobile'].includes(value),
    },

    /**
     * Current page to show (if widget supports multiple pages)
     */
    current_page: {
      type: String,
      default: null,
    },

    /**
     * Custom width for the container
     */
    width: {
      type: [String, Number],
      default: null,
    },

    /**
     * Custom height for the container
     */
    height: {
      type: [String, Number],
      default: null,
    },

    /**
     * Scale factor for the preview
     */
    scale: {
      type: Number,
      default: 1,
    },

    /**
     * Whether to disable interactions (for template previews)
     */
    disable_interactions: {
      type: Boolean,
      default: false,
    },

    /**
     * Placeholder text when loading
     */
    placeholder_text: {
      type: String,
      default: 'Loading preview...',
    },
  },

  data() {
    return {
      iframe_key: 0,
      is_iframe_loaded: false,
      widget_ready: false,
      message_handler: null,
    };
  },

  computed: {
    container_style() {
      const styles = {};

      const viewport_width = this.width
        ? typeof this.width === 'number'
          ? this.width
          : parseInt(this.width)
        : 1200;
      const viewport_height = this.height
        ? typeof this.height === 'number'
          ? this.height
          : parseInt(this.height)
        : 1100;

      styles.width = `${viewport_width * this.scale}px`;
      styles.height = `${viewport_height * this.scale}px`;
      styles.overflow = 'hidden';
      styles.position = 'relative';

      return styles;
    },

    wrapper_style() {
      const styles = {};

      const viewport_width = this.width
        ? typeof this.width === 'number'
          ? this.width
          : parseInt(this.width)
        : 1200;
      const viewport_height = this.height
        ? typeof this.height === 'number'
          ? this.height
          : parseInt(this.height)
        : 1100;

      styles.width = `${viewport_width}px`;
      styles.height = `${viewport_height}px`;

      if (this.scale !== 1) {
        styles.transform = `scale(${this.scale})`;
        styles.transformOrigin = 'top left';
      }

      return styles;
    },

    frame_class() {
      return {
        'mobile-frame': this.preview_mode === 'mobile',
        'tablet-frame': this.preview_mode === 'tablet',
        'tablet-landscape-frame': this.preview_mode === 'tablet-landscape',
        'disable-interactions': this.disable_interactions,
      };
    },

    widget_src() {
      if (!this.widget_config) {
        return null;
      }

      return this.build_src(this.preview_mode, process.env.NODE_ENV === 'development');
    },
  },

  watch: {
    widget_config: {
      handler() {
        this.refreshIframe();
      },
      deep: true,
    },

    preview_mode() {
      this.refreshIframe();
    },

    current_page() {
      this.sendPageChangeMessage();
    },
  },

  mounted() {
    this.message_handler = createParentMessageHandler({
      [WIDGET_TO_PARENT.READY]: () => {
        this.widget_ready = true;
        this.sendSettingsToWidget();
        this.$emit('widget-ready');
      },
      [WIDGET_TO_PARENT.FORM_SUBMITTED]: data => {
        this.$emit('form-submitted', data);
      },
      [WIDGET_TO_PARENT.FORM_RESET]: () => {
        this.$emit('form-reset');
      },
      [WIDGET_TO_PARENT.PAGE_CHANGED]: page => {
        this.$emit('page-changed', page);
      },
      [WIDGET_TO_PARENT.ERROR]: error => {
        this.$emit('widget-error', error);
      },
    });

    window.addEventListener('message', this.message_handler);
  },

  beforeDestroy() {
    if (this.message_handler) {
      window.removeEventListener('message', this.message_handler);
    }
  },

  methods: {
    refreshIframe() {
      this.iframe_key += 1;
      this.is_iframe_loaded = false;
      this.widget_ready = false;
    },

    onIframeLoad() {
      this.is_iframe_loaded = true;
      this.$emit('iframe-loaded');

      this.$nextTick(() => {
        this.sendSettingsToWidget();
      });
    },

    sendSettingsToWidget() {
      if (!this.is_iframe_loaded || !this.$refs['widget-iframe']) {
        return;
      }

      this.send_settings(this.$refs['widget-iframe'], this.widget_config);
      this.sendPageChangeMessage();
    },

    sendPageChangeMessage() {
      if (!this.is_iframe_loaded || !this.$refs['widget-iframe'] || !this.send_page_change) {
        return;
      }

      if (this.current_page) {
        this.send_page_change(this.$refs['widget-iframe'], this.current_page);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.widget-iframe-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.iframe-scaler {
  position: relative;
  display: grid;
  align-items: center;
}

.iframe-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  background: white;
  border-radius: 4px;
  overflow: hidden;
}

.iframe-wrapper.mobile-frame {
  width: 375px;
  height: 667px;
  border: 8px solid #333;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  background: #111;
  padding: 8px;
  margin: 0 auto;
}

.iframe-wrapper.tablet-frame {
  width: 768px;
  height: 1024px;
  border: 6px solid #2a2a2a;
  border-radius: 16px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
  background: #111;
  padding: 10px;
  margin: 0 auto;
}

.iframe-wrapper.tablet-landscape-frame {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.iframe-wrapper.mobile-frame::after {
  content: '';
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 6px;
  background: #333;
  border-radius: 3px;
  z-index: 3;
}

.widget-iframe {
  width: 100%;
  height: 100%;
  border: none;
  background: white;
}

.iframe-wrapper.disable-interactions {
  pointer-events: none;
  user-select: none;
}

.iframe-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: #f5f5f5;
  color: #666;
}
</style>
